import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Clock, Facebook, Instagram, Loader2, Mail, MapPin, MessageCircle, Phone, Star, Truck } from "lucide-react";
import Reveal from "@/components/Reveal";
import { SITE } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { nombre: "", telefono: "", email: "", mensaje: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("¡Mensaje enviado! Te contactaremos muy pronto.", {
        duration: 5000,
      });
      setForm(INITIAL);
    } catch {
      toast.error("No pudimos enviar tu mensaje. Inténtalo de nuevo o llámanos al +52 452 132 6712.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contacto"
      className="border-t border-cream-border bg-cream-soft py-24 lg:py-32"
      data-testid="contact-section"
    >
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2 lg:px-12">
        <div>
          <Reveal>
            <p className="eyebrow">Contacto</p>
            <h2 className="mt-5 font-serif text-4xl leading-tight tracking-tight text-olive sm:text-5xl lg:text-6xl">
              Hablemos de tu cocina.
            </h2>
            <p className="mt-6 max-w-md leading-relaxed text-olive-mid">
              Escríbenos por WhatsApp, llámanos o visita nuestro showroom. Te
              atiende Antonio, sin compromiso y sin costo.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-4">
            <a
              href={SITE.phoneHref}
              data-testid="contact-phone-button"
              className="btn-primary"
            >
              <Phone className="h-5 w-5" />
              Llámanos: {SITE.phoneDisplay}
            </a>
            <a
              href={SITE.whatsappHref}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-button"
              className="btn-outline"
            >
              <MessageCircle className="h-4 w-4" />
              O escríbenos por WhatsApp
            </a>
          </Reveal>

          <Reveal delay={0.2} className="mt-12 space-y-6">
            <div className="flex items-start gap-4" data-testid="contact-info-address">
              <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" strokeWidth={1.5} />
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-olive-mid">
                  Showroom
                </p>
                <p className="mt-1 text-olive">{SITE.address}</p>
                <a
                  href={SITE.mapsHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-maps-link"
                  className="mt-2 inline-flex items-center gap-1 text-sm text-terracotta underline-offset-4 transition-colors duration-200 hover:text-terracotta-dark hover:underline"
                >
                  Ver en Google Maps
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-info-hours">
              <Clock className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" strokeWidth={1.5} />
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-olive-mid">
                  Horario
                </p>
                {SITE.hours.map((h) => (
                  <p key={h.days} className="mt-1 text-olive">
                    {h.days} · {h.time}
                  </p>
                ))}
                <p className="mt-2 max-w-sm text-xs leading-relaxed text-olive-mid">
                  {SITE.holidays}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-info-email">
              <Mail className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" strokeWidth={1.5} />
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-olive-mid">
                  Correo
                </p>
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="contact-email-link"
                  className="mt-1 block text-olive transition-colors duration-200 hover:text-terracotta"
                >
                  {SITE.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-info-service-areas">
              <Truck className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" strokeWidth={1.5} />
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-olive-mid">
                  Zona de servicio
                </p>
                <p className="mt-1 max-w-sm text-olive">
                  Servicio en diferentes partes de la república:{" "}
                  {SITE.serviceAreas.join(", ")} y más.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.3} className="mt-10 flex items-center gap-4">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-olive-mid">
              Síguenos
            </p>
            <a
              href={SITE.facebook}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-facebook-link"
              aria-label="Facebook de Toscana Cocinas Integrales"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-olive/15 text-olive transition-colors duration-300 hover:bg-olive hover:text-cream"
            >
              <Facebook className="h-5 w-5" strokeWidth={1.5} />
            </a>
            {SITE.instagram && (
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-instagram-link"
                aria-label="Instagram de Toscana Cocinas Integrales"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-olive/15 text-olive transition-colors duration-300 hover:bg-olive hover:text-cream"
              >
                <Instagram className="h-5 w-5" strokeWidth={1.5} />
              </a>
            )}
            {SITE.tiktok && (
              <a
                href={SITE.tiktok}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-tiktok-link"
                aria-label="TikTok de Toscana Cocinas Integrales"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-olive/15 text-olive transition-colors duration-300 hover:bg-olive hover:text-cream"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                  <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.25-.02-.5-.02-.76.02-1.74.72-3.45 1.92-4.72 1.42-1.51 3.5-2.38 5.57-2.19.16-1.63.94-3.18 2.2-4.26 1.34-1.14 3.14-1.78 4.9-1.72z" />
                </svg>
              </a>
            )}
          </Reveal>

          <Reveal delay={0.25} className="mt-10">
            <div
              data-testid="visit-invite-card"
              className="rounded-3xl border border-cream-border bg-card p-8"
            >
              <h3 className="font-serif text-2xl text-olive">
                Ven al taller y pregunta lo que quieras
              </h3>
              <p className="mt-3 leading-relaxed text-olive-mid">
                Conoce materiales, acabados y proyectos en vivo. Estamos en
                Uruapan y con gusto resolvemos todas tus dudas, sin
                compromiso.
              </p>
              <a
                href={SITE.googleBusiness || SITE.mapsHref}
                target="_blank"
                rel="noreferrer"
                data-testid="visit-invite-maps-button"
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-olive/25 px-6 py-3 text-sm font-medium text-olive transition-colors duration-300 hover:border-olive hover:bg-olive hover:text-cream"
              >
                Cómo llegar
                <ArrowUpRight className="h-4 w-4" />
              </a>
              {SITE.googleBusiness && (
                <a
                  href={SITE.googleBusiness}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="google-review-button"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3 text-sm font-medium text-cream transition-colors duration-300 hover:bg-terracotta-dark"
                >
                  <Star className="h-4 w-4 fill-cream" />
                  Déjanos tu reseña en Google
                </a>
              )}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} y={36}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl border border-cream-border bg-card p-8 shadow-[0_24px_60px_rgba(13,21,38,0.08)] lg:p-10"
          >
            <h3 className="font-serif text-3xl text-olive">
              Déjanos tu mensaje
            </h3>
            <p className="mt-2 text-sm text-olive-mid">
              Cuéntanos qué necesitas y te respondemos el mismo día hábil.
            </p>

            <div className="mt-8 space-y-5">
              <div>
                <label htmlFor="nombre" className="text-xs font-medium uppercase tracking-[0.2em] text-olive-mid">
                  Nombre *
                </label>
                <input
                  id="nombre"
                  type="text"
                  required
                  minLength={2}
                  value={form.nombre}
                  onChange={update("nombre")}
                  data-testid="contact-input-nombre"
                  placeholder="Tu nombre"
                  className="mt-2 w-full rounded-xl border border-cream-border bg-cream-soft px-4 py-3 text-olive outline-none transition-colors duration-200 placeholder:text-olive-mid/50 focus:border-terracotta"
                />
              </div>
              <div>
                <label htmlFor="telefono" className="text-xs font-medium uppercase tracking-[0.2em] text-olive-mid">
                  WhatsApp o teléfono *
                </label>
                <input
                  id="telefono"
                  type="tel"
                  required
                  minLength={6}
                  value={form.telefono}
                  onChange={update("telefono")}
                  data-testid="contact-input-telefono"
                  placeholder="(555) 000-0000"
                  className="mt-2 w-full rounded-xl border border-cream-border bg-cream-soft px-4 py-3 text-olive outline-none transition-colors duration-200 placeholder:text-olive-mid/50 focus:border-terracotta"
                />
              </div>
              <div>
                <label htmlFor="email" className="text-xs font-medium uppercase tracking-[0.2em] text-olive-mid">
                  Correo (opcional)
                </label>
                <input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-input-email"
                  placeholder="tu@correo.com"
                  className="mt-2 w-full rounded-xl border border-cream-border bg-cream-soft px-4 py-3 text-olive outline-none transition-colors duration-200 placeholder:text-olive-mid/50 focus:border-terracotta"
                />
              </div>
              <div>
                <label htmlFor="mensaje" className="text-xs font-medium uppercase tracking-[0.2em] text-olive-mid">
                  ¿Qué necesitas? *
                </label>
                <textarea
                  id="mensaje"
                  required
                  minLength={5}
                  rows={4}
                  value={form.mensaje}
                  onChange={update("mensaje")}
                  data-testid="contact-input-mensaje"
                  placeholder="Ej. Quiero renovar la cocina de mi departamento, unos 12 m²..."
                  className="mt-2 w-full resize-none rounded-xl border border-cream-border bg-cream-soft px-4 py-3 text-olive outline-none transition-colors duration-200 placeholder:text-olive-mid/50 focus:border-terracotta"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-70"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  "Enviar mensaje"
                )}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
